import React, { useState } from 'react'
import { Pressable, Text, TouchableOpacity, View } from 'react-native'
import { AppModal } from '../ui/AppModal'
import { SETUP_CONTROL, SetupField, type SetupHelp, type SetupLayout } from './setup/SetupControls'

/**
 * A dropdown setup setting: the label (with an optional "?" beside it) and the
 * current value as a pill that opens a short list of options. Shared by fog of
 * war, the boot timer and the setup page's other dropdowns so they can't drift.
 */
export function CompactOptionSelector<T extends string | number | null>({
  title, value, options, onChange, pillTestID, optionTestID, helpLabel, helpMessage,
  layout = 'stacked', changed, disabled, note, noteTone, noteUnderControl, separated,
}: {
  title: string
  value: T
  options: ReadonlyArray<{ value: T; label: string; detail?: string }>
  onChange: (value: T) => void
  /** testID of the pill. Not `testID`: that would also tag this wrapper. */
  pillTestID: string
  /** testID of each option row. */
  optionTestID: (value: T) => string
  helpLabel?: string
  /** The help box text; a list renders as separate paragraphs. */
  helpMessage?: string | readonly string[]
  layout?: SetupLayout
  changed?: boolean
  disabled?: boolean
  note?: string | null
  noteTone?: 'muted' | 'warn'
  /** Row layout: note under the dropdown, right-aligned. */
  noteUnderControl?: boolean
  separated?: boolean
}) {
  const [optionsOpen, setOptionsOpen] = useState(false)
  const label = options.find(option => option.value === value)?.label ?? options[0]?.label ?? ''
  const help: SetupHelp | undefined = helpLabel && helpMessage ? { label: helpLabel, title, message: helpMessage } : undefined
  return <>
    <SetupField label={title} help={help} changed={changed} layout={layout} note={note} noteTone={noteTone} noteUnderControl={noteUnderControl} separated={separated}>
      <TouchableOpacity accessibilityRole="button" accessibilityLabel={`${title}: ${label}`} accessibilityState={{ disabled: !!disabled }}
        testID={pillTestID} disabled={disabled} onPress={() => setOptionsOpen(true)}
        className="rounded-md border-2 border-olive bg-paper-up items-center justify-center"
        style={{ ...SETUP_CONTROL[layout], paddingHorizontal: 24, opacity: disabled ? 0.45 : 1 }}>
        <Text className="font-typed text-ink text-sm" numberOfLines={1}>{label}</Text>
        <Text className="font-typed text-ink-soft text-xs" style={{ position: 'absolute', right: 9 }}>▾</Text>
      </TouchableOpacity>
    </SetupField>
    <AppModal visible={optionsOpen} animationType="fade" transparent onRequestClose={() => setOptionsOpen(false)}>
      <View className="flex-1 items-center justify-center px-5" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
        <Pressable accessibilityLabel={`Close ${title} options`} onPress={() => setOptionsOpen(false)} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />
        <View className="w-full rounded-xl border-2 border-olive bg-paper p-4 gap-1" style={{ maxWidth: 360 }}>
          <Text className="font-stencil text-ink text-base uppercase mb-2">{title}</Text>
          {options.map(option => <TouchableOpacity key={String(option.value)} testID={optionTestID(option.value)}
            accessibilityRole="button" accessibilityLabel={option.label} accessibilityState={{ selected: value === option.value }}
            onPress={() => { onChange(option.value); setOptionsOpen(false) }} className="flex-row justify-between items-center rounded-md px-3 py-2 bg-paper-up">
            <View className="flex-1">
              <Text className="font-typed text-ink">{option.label}</Text>
              {option.detail ? <Text className="font-typed text-ink-soft text-xs">{option.detail}</Text> : null}
            </View>
            <Text className="font-stencil text-blood">{value === option.value ? '✓' : ''}</Text>
          </TouchableOpacity>)}
        </View>
      </View>
    </AppModal>
  </>
}
